import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';

type ToggleOption = { label: string; route: string; default?: boolean };

@Component({
  selector: 'app-toggle-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toggle-button.component.html',
  styleUrl: './toggle-button.component.scss',
})
export class ToggleButtonComponent implements OnInit, OnDestroy {
  @Input() data!: any;

  options: ToggleOption[] = [];
  displayOptions: ToggleOption[] = [];
  topOptions: ToggleOption[] = [];
  bottomOptions: ToggleOption[] = [];
  isSpecialSplitLayout = false;
  activeRoute = '';
  private sub = new Subscription();

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.options = this.data?.options || [];
    this.configureLayout(this.options);
    this.activeRoute = this.router.url.split('?')[0];

    // keep activeRoute updated on navigation
    this.sub.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => (this.activeRoute = this.router.url.split('?')[0]))
    );

    // if current route is NOT one of the toggle routes, go to default
    const isOnAnyToggleRoute = this.options.some(o => o.route === this.activeRoute);
    const defaultOption = this.options.find(o => o.default);

    if (!isOnAnyToggleRoute && defaultOption) {
      this.router.navigateByUrl(defaultOption.route);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  isActive(option: ToggleOption): boolean {
    return this.activeRoute === option.route;
  }

  onToggle(option: ToggleOption): void {
    if (this.isActive(option)) return;
    this.router.navigateByUrl(option.route);
  }

  private configureLayout(options: ToggleOption[]): void {
    const indexedOptions = options.map((option, index) => ({ option, index }));
    const bottomPriorityOptions = indexedOptions
      .filter((o) => this.isBottomPriorityLabel(o.option?.label))
      .map((o) => o.option)
      .sort((a, b) => this.getBottomPriorityOrder(a.label) - this.getBottomPriorityOrder(b.label));
    const topRowOptions = indexedOptions
      .filter((o) => !this.isBottomPriorityLabel(o.option?.label))
      .sort((a, b) => this.compareByFixedPosition(a.option, b.option, a.index, b.index))
      .map((o) => o.option);

    if (bottomPriorityOptions.length > 0 && topRowOptions.length > 0) {
      this.isSpecialSplitLayout = true;
      this.topOptions = topRowOptions;
      this.bottomOptions = bottomPriorityOptions;
      this.displayOptions = [];
      return;
    }

    this.isSpecialSplitLayout = false;
    this.topOptions = topRowOptions;
    this.bottomOptions = [];
    this.displayOptions = this.getDisplayOptions(topRowOptions);
  }

  private isBottomPriorityLabel(label: string): boolean {
    const normalized = String(label || '').trim();
    return normalized === '$' || normalized === '$+%';
  }

  private getBottomPriorityOrder(label: string): number {
    const normalized = String(label || '').trim();
    if (normalized === '$') return 0;
    if (normalized === '$+%') return 1;
    return 99;
  }

  private compareByFixedPosition(
    left: ToggleOption,
    right: ToggleOption,
    leftIndex: number,
    rightIndex: number
  ): number {
    const leftRank = this.getLeftPriorityRank(left?.label);
    const rightRank = this.getLeftPriorityRank(right?.label);

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return leftIndex - rightIndex;
  }

  private getLeftPriorityRank(label: string): number {
    const normalized = String(label || '').toLowerCase().trim();
    if (normalized.includes('overview')) return 0;
    if (normalized.includes('quarterly')) return 1;
    if (normalized.includes('by region')) return 2;
    if (normalized.includes('by segment')) return 3;
    if (normalized.includes('by engine model')) return 4;
    return 10;
  }

  private getDisplayOptions(options: ToggleOption[]): ToggleOption[] {
    return options;
  }
}
